import React, { useEffect, type ReactElement, useState } from 'react'

import './packages.css'
import packageService from '../../services/packageService'
import { type PackageType } from '../../utils/types'
import AdminButton from '../utils/adminButton/AdminButton'
import ListPackage from './listPackage/ListPackage'
import Modal from '../utils/modal/Modal'
import AddPackage from './addPackage/AddPackage'
import { ReactComponent as ImportIcon } from '../../assets/import_icon.svg'
import InfoModal from '../utils/infoModal/InfoModal'
import { useSelector } from 'react-redux'
import { type RootState } from '../../store/store'

const Packages = (): ReactElement => {
  const [packages, setPackages] = useState<PackageType[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false)
  const [modalText, setmodalText] = useState<any>()
  const [shouldCloseModal, setShouldCloseModal] = useState(false)
  const images = useSelector((state: RootState) => state.images.images)

  const handleAddPackage = async (): Promise<void> => {
    try {
      const response = await packageService.getAllPackages()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setPackages(response)
    } catch (error) {
      console.error('Failed to fetch audiences:', error)
    }
  }

  useEffect(() => {
    const fetchPackages = async (): Promise<void> => {
      try {
        const response = await packageService.getAllPackages()
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        setPackages(response)
      } catch (error) {
        console.error('Failed to fetch audiences:', error)
      }
    }
    void fetchPackages()
  }, [])

  const onDrop = (e: React.DragEvent<HTMLLabelElement>): void => {
    e.preventDefault()
    const files = e.dataTransfer.files
    if (files.length > 0) {
      setSelectedFile(files[0])
    }
  }

  const onDragOver = (e: React.DragEvent<HTMLLabelElement>): void => {
    e.preventDefault()
  }

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    if (event.target.files) {
      setSelectedFile(event.target.files[0])
    }
  }

  const handleUpload = async (): Promise<void> => {
    if (selectedFile) {
      const fileName = selectedFile.name
      const fileExtension = fileName.split('.').pop()?.toLowerCase()

      if (fileExtension !== 'xlsx') {
        setIsInfoModalOpen(true)
        setShouldCloseModal(false)
        setmodalText('Le format du fichier n\'est pas accepté.')
        return
      }

      const formData = new FormData()
      formData.append('file', selectedFile)

      try {
        const imageUrls = images.map((image) => image.url)
        await packageService.importPackages(formData, imageUrls)
        setIsInfoModalOpen(true)
        setmodalText('Packages importés avec succès')
        setSelectedFile(null)
        await handleAddPackage()
        setShouldCloseModal(true)
      } catch (error: any) {
        setIsInfoModalOpen(true)
        setShouldCloseModal(false)
        let errorMessage =
          "Une erreur est survenue lors de l'importation des packages."

        if (error?.isAxiosError) {
          const errorMessages = error.response?.data?.errors
          if (Array.isArray(errorMessages)) {
            errorMessage = errorMessages.join('\n')
          } else {
            errorMessage = error.response?.data?.message || errorMessage
          }
        } else {
          errorMessage = error.message || errorMessage
        }
        const errorMessagesArray = errorMessage.split('\n')

        const formattedErrorMessages = errorMessagesArray.map((msg, index) => (
          <div style={{ padding: '5px' }} key={index}>
            {msg}
          </div>
        ))

        setmodalText(formattedErrorMessages)
      }
    }
  }

  const handleExport = async (): Promise<void> => {
    try {
      await packageService.downloadExportedPackages()
    } catch (error: any) {
      console.error('Failed to export data ', error)
    }
  }

  return (
    <div className='packages-container'>
      <AdminButton
        onClick={() => {
          setIsModalOpen(true)
        }}
      >
        CREER PACKAGE
      </AdminButton>
      <AdminButton
        onClick={() => {
          setIsImportModalOpen(true)
        }}
      >
        IMPORT PACKAGE
      </AdminButton>
      <AdminButton onClick={handleExport}>EXPORT PACKAGE</AdminButton>

      <ListPackage packagesList={packages} />

      <Modal
        isOpen={isImportModalOpen}
        title='Import Package'
        onClose={() => {
          setIsImportModalOpen(false)
          setSelectedFile(null)
        }}
      >
        <div className='flex flex-col items-center justify-center space-y-4'>
          <label
            className={`label ${
              selectedFile ? 'border-green-500' : 'border-gray-300'
            }`}
            htmlFor='file_upload_2'
            onDragOver={onDragOver}
            onDrop={onDrop}
          >
            <span className='span-container'>
              <ImportIcon
                className={`icon ${
                  selectedFile ? 'text-green-600' : 'text-gray-600'
                }`}
              />
              <span
                className={`font-medium ${
                  selectedFile ? 'text-green-600' : 'text-gray-600'
                }`}
              >
                {selectedFile ? selectedFile.name : 'Importer un fichier .xlsx'}
              </span>
            </span>
            <input
              className='hidden'
              name='file_upload_2'
              onChange={handleFileChange}
              type='file'
              id='file_upload_2'
              multiple
            />
          </label>
          <AdminButton
            className='import-products-button'
            onClick={handleUpload}
          >
            Importer
          </AdminButton>
        </div>
      </Modal>

      <Modal
        isOpen={isModalOpen}
        title='Ajouter un nouveau package'
        onClose={() => {
          setIsModalOpen(false)
        }}
      >
        <AddPackage
          closeModal={() => {
            setIsModalOpen(false)
          }}
          onAddPackage={handleAddPackage}
        />
      </Modal>

      <InfoModal
        isOpen={isInfoModalOpen}
        title='Import packages'
        message={modalText}
        onClose={() => {
          setIsInfoModalOpen(false)

          if (shouldCloseModal) {
            setIsImportModalOpen(false)
          }
        }}
      />
    </div>
  )
}

export default Packages
